import React, { useState, useEffect } from 'react';
import { Badge, BlockStack, Button, Card, Checkbox, DataTable, FormLayout, InlineStack, Modal, ProgressBar, Text, TextField, Toast } from '@shopify/polaris';
import { useAuth } from '../AuthComponent';
import { api } from './apiClient';
import { useStore } from './StoreContext';

function fmtTs(ts) {
    if (!ts) return 'Never';
    return new Date(ts).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export default function StoresSection() {
    const { stores, reloadStores } = useStore();
    const { permissions } = useAuth();
    const isAdmin = (permissions || []).includes('admin');
    const prevRunningRef = React.useRef({});
    const [showAdd, setShowAdd] = useState(false);
    const [form, setForm] = useState({
        merchant_id: '', account_name: '', display_name: '',
        refresh_token: '', marketplace_id: 'ATVPDKIKX0DER',
    });
    const [toast, setToast] = useState('');

    const [editing, setEditing] = useState(null);
    const [editForm, setEditForm] = useState(null);
    const [updateTokenMode, setUpdateTokenMode] = useState(false);

    const [syncStatus, setSyncStatus] = useState({});

    useEffect(() => {
        const tick = async () => {
            try {
                const next = await api.syncStatus();
                // If any (store,type) went from running → not-running since
                // the last tick, refresh the store list so Last X
                // timestamps reflect the new DB state.
                let shouldReload = false;
                for (const [sid, byType] of Object.entries(next)) {
                    for (const [t, slot] of Object.entries(byType)) {
                        const key = `${sid}:${t}`;
                        const wasRunning = prevRunningRef.current[key] === true;
                        const isRunning = slot?.status === 'running';
                        if (wasRunning && !isRunning) shouldReload = true;
                        prevRunningRef.current[key] = isRunning;
                    }
                }
                setSyncStatus(next);
                if (shouldReload) reloadStores();
            } catch (e) {
                // ignore transient errors
            }
        };
        tick();
        const id = setInterval(tick, 2000);
        return () => clearInterval(id);
    }, [reloadStores]);

    const submitAdd = async () => {
        try {
            await api.createStore(form);
            setShowAdd(false);
            setForm({ merchant_id: '', account_name: '', display_name: '', refresh_token: '', marketplace_id: 'ATVPDKIKX0DER' });
            reloadStores();
            setToast('Store added');
        } catch (e) {
            setToast(e.status === 403 ? 'Only admins can add stores' : 'Failed to add store');
        }
    };

    const openEdit = (s) => {
        setEditing(s);
        setEditForm({
            merchant_id: s.merchant_id,
            account_name: s.account_name,
            display_name: s.display_name,
            marketplace_id: s.marketplace_id,
            active: s.active,
            use_keepa: !!s.use_keepa,
            fbm_label_cost: s.fbm_label_cost != null ? String(Number(s.fbm_label_cost)) : '',
            sp_api_secret_rotated_at: s.sp_api_secret_rotated_at || '',
            repricer_landed: s.repricer_landed !== false,
            refresh_token: '',
        });
        setUpdateTokenMode(false);
    };

    const submitEdit = async () => {
        const payload = {
            merchant_id: editForm.merchant_id,
            account_name: editForm.account_name,
            display_name: editForm.display_name,
            marketplace_id: editForm.marketplace_id,
            active: editForm.active,
            use_keepa: editForm.use_keepa,
            repricer_landed: editForm.repricer_landed,
            fbm_label_cost: editForm.fbm_label_cost.trim() === '' ? null : Number(editForm.fbm_label_cost),
        };
        // Only a store with its own SP-API app has a secret of its own to date.
        if (editing.own_app && editForm.sp_api_secret_rotated_at) {
            payload.sp_api_secret_rotated_at = editForm.sp_api_secret_rotated_at;
        }
        if (updateTokenMode && editForm.refresh_token) {
            payload.refresh_token = editForm.refresh_token;
        }
        try {
            await api.updateStore(editing.id, payload);
            setEditing(null);
            reloadStores();
            setToast('Store updated');
        } catch (e) {
            setToast(e.status === 403 ? 'Only admins can edit stores' : 'Failed to update store');
        }
    };

    const rows = stores.map(s => {
        const cells = [
            <BlockStack gap="050" key="store">
                <Text as="span" variant="bodyMd" fontWeight="semibold">{s.display_name}</Text>
                <Text as="span" variant="bodySm" tone="subdued">{s.account_name}</Text>
                <Text as="span" variant="bodySm" tone="subdued"><code>{s.merchant_id}</code></Text>
                {s.fbm_label_cost != null && (
                    <Text as="span" variant="bodySm" tone="subdued">FBM label ${Number(s.fbm_label_cost).toFixed(2)}/order</Text>
                )}
            </BlockStack>,
            <Badge key="status" tone={s.active ? 'success' : 'critical'}>{s.active ? 'Active' : 'Inactive'}</Badge>,
            <SyncCell key="amazon" storeId={s.id} type="amazon" label="Sync Amazon" lastAt={s.last_amazon_sync} live={syncStatus[s.id]?.amazon} setToast={setToast} />,
            <SyncCell key="competitors" storeId={s.id} type="competitors" label="Sync Competitors" lastAt={s.last_competitor_sync} live={syncStatus[s.id]?.competitors} setToast={setToast} />,
            <SyncCell key="repricer" storeId={s.id} type="repricer" label="Sync Repricer" lastAt={s.last_repricer_sync} live={syncStatus[s.id]?.repricer} setToast={setToast} />,
        ];
        if (isAdmin) cells.push(<Button key="edit" size="slim" onClick={() => openEdit(s)}>Edit</Button>);
        return cells;
    });
    const headings = ['Store', 'Status', 'Amazon', 'Competitors', 'Repricer', ...(isAdmin ? ['Edit'] : [])];

    return (
        <Card padding="0">
            <div style={{ padding: 'var(--p-space-400)' }}>
                <InlineStack align="space-between" blockAlign="center">
                    <BlockStack gap="050">
                        <Text as="h3" variant="headingMd">Amazon stores</Text>
                        <Text as="p" tone="subdued">Each store syncs on its own; the Repricer pull covers all of them.</Text>
                    </BlockStack>
                    {isAdmin && <Button variant="primary" onClick={() => setShowAdd(true)}>Add store</Button>}
                </InlineStack>
            </div>
            <DataTable
                columnContentTypes={headings.map(() => 'text')}
                headings={headings}
                rows={rows}
                verticalAlign="top"
                increasedTableDensity
            />

            <Modal
                open={showAdd}
                onClose={() => setShowAdd(false)}
                title="Add Amazon store"
                primaryAction={{ content: 'Save', onAction: submitAdd }}
                secondaryActions={[{ content: 'Cancel', onAction: () => setShowAdd(false) }]}
            >
                <Modal.Section>
                    <FormLayout>
                        <TextField label="Merchant ID" value={form.merchant_id}
                            onChange={v => setForm(f => ({ ...f, merchant_id: v }))} autoComplete="off" />
                        <TextField label="Account name" value={form.account_name}
                            onChange={v => setForm(f => ({ ...f, account_name: v }))} autoComplete="off" />
                        <TextField label="Display name" value={form.display_name}
                            onChange={v => setForm(f => ({ ...f, display_name: v }))} autoComplete="off" />
                        <TextField label="Refresh token" type="password"
                            value={form.refresh_token}
                            onChange={v => setForm(f => ({ ...f, refresh_token: v }))} autoComplete="off" />
                        <TextField label="Marketplace" value={form.marketplace_id}
                            onChange={v => setForm(f => ({ ...f, marketplace_id: v }))} autoComplete="off" />
                    </FormLayout>
                </Modal.Section>
            </Modal>

            <Modal
                open={!!editing}
                onClose={() => setEditing(null)}
                title={editing ? `Edit store: ${editing.display_name}` : 'Edit store'}
                primaryAction={{ content: 'Save', onAction: submitEdit }}
                secondaryActions={[{ content: 'Cancel', onAction: () => setEditing(null) }]}
            >
                <Modal.Section>
                    {editForm && (
                        <FormLayout>
                            <TextField label="Merchant ID" value={editForm.merchant_id} disabled
                                helpText="The merchant id is the key Repricer rows are routed by; it cannot be changed here."
                                autoComplete="off" />
                            <TextField label="Account name" value={editForm.account_name}
                                onChange={v => setEditForm(f => ({ ...f, account_name: v }))} autoComplete="off" />
                            <TextField label="Display name" value={editForm.display_name}
                                onChange={v => setEditForm(f => ({ ...f, display_name: v }))} autoComplete="off" />
                            <TextField label="Marketplace" value={editForm.marketplace_id}
                                onChange={v => setEditForm(f => ({ ...f, marketplace_id: v }))} autoComplete="off" />
                            <TextField label="FBM shipping label cost (per order)" type="number" prefix="$"
                                min={0} step={0.01} value={editForm.fbm_label_cost}
                                onChange={v => setEditForm(f => ({ ...f, fbm_label_cost: v }))}
                                helpText="Average label cost for merchant-fulfilled orders. Subtracted in GM% for this store's FBM listings only. Leave blank for none."
                                autoComplete="off" />
                            <Checkbox
                                label="Active"
                                checked={editForm.active}
                                onChange={v => setEditForm(f => ({ ...f, active: v }))}
                            />
                            <Checkbox
                                label="Use Keepa"
                                helpText="Pull competitor stock and offers from Keepa for this store."
                                checked={editForm.use_keepa}
                                onChange={v => setEditForm(f => ({ ...f, use_keepa: v }))}
                            />
                            <Checkbox
                                label="Send landed min/max to Repricer (item price + shipping)"
                                helpText={"Repricer reads min and max as the price the buyer pays, shipping included. With this on, the min/max you type in the Pricing Hub are item prices: the Hub adds each FBM listing's shipping when it sends them to Repricer and takes it off again when it reads them back. Example with $10 shipping: a $10 min and $40 max in the Hub reach Repricer as $20 and $50. FBA listings (free shipping to the buyer) are never changed. Off = sent exactly as typed."}
                                checked={editForm.repricer_landed}
                                onChange={v => setEditForm(f => ({ ...f, repricer_landed: v }))}
                            />
                            {editing?.own_app ? (
                                <TextField label="SP-API secret rotated on" type="date"
                                    value={editForm.sp_api_secret_rotated_at}
                                    onChange={v => setEditForm(f => ({ ...f, sp_api_secret_rotated_at: v }))}
                                    helpText="This store has its own SP-API app. Amazon expires its client secret 180 days after this date; the countdown is on the credentials card."
                                    autoComplete="off" />
                            ) : (
                                <Text as="p" tone="subdued">Uses the shared SP-API app; its secret's rotation date is SP_API_SECRET_ROTATED_AT in .env.</Text>
                            )}
                            {!updateTokenMode ? (
                                <TextField
                                    label="Refresh token"
                                    value="•••••••• (stored encrypted)"
                                    disabled
                                    autoComplete="off"
                                    connectedRight={<Button onClick={() => setUpdateTokenMode(true)}>Update token</Button>}
                                />
                            ) : (
                                <TextField
                                    label="New refresh token"
                                    type="password"
                                    value={editForm.refresh_token}
                                    onChange={v => setEditForm(f => ({ ...f, refresh_token: v }))}
                                    autoComplete="off"
                                    helpText="Leave blank to keep the existing token."
                                    connectedRight={<Button onClick={() => { setUpdateTokenMode(false); setEditForm(f => ({ ...f, refresh_token: '' })); }}>Keep current</Button>}
                                />
                            )}
                        </FormLayout>
                    )}
                </Modal.Section>
            </Modal>

            {toast && <Toast content={toast} onDismiss={() => setToast('')} duration={3000} />}
        </Card>
    );
}

function fmtElapsed(fromIso) {
    if (!fromIso) return '';
    const sec = Math.max(0, Math.floor((Date.now() - new Date(fromIso).getTime()) / 1000));
    const h = Math.floor(sec / 3600), m = Math.floor((sec % 3600) / 60);
    return h ? `${h}h ${m}m` : m ? `${m}m` : `${sec}s`;
}

function SyncCell({ storeId, type, label, lastAt, live, setToast }) {
    const running = live?.status === 'running';
    const hasTotal = running && live.total > 0 && live.current != null;
    const pct = hasTotal ? Math.min(100, Math.round((live.current / live.total) * 100)) : null;
    return (
        <BlockStack gap="100">
            {running ? (
                <BlockStack gap="050">
                    <Text as="span" variant="bodySm" fontWeight="semibold">{live.phase ? live.phase[0].toUpperCase() + live.phase.slice(1) : 'Running'}</Text>
                    {live.detail && !hasTotal && <Text as="span" variant="bodySm" tone="subdued" truncate>{live.detail}</Text>}
                    {hasTotal && (
                        <>
                            <div style={{ width: 180 }}><ProgressBar progress={pct} size="small" /></div>
                            <Text as="span" variant="bodySm" tone="subdued">{live.current.toLocaleString()} / {live.total.toLocaleString()} ({pct}%)</Text>
                        </>
                    )}
                    <Text as="span" variant="bodySm" tone="subdued">Running {fmtElapsed(live.started_at)}</Text>
                </BlockStack>
            ) : (
                <BlockStack gap="050">
                    <Text as="span" variant="bodySm" tone="subdued">{fmtTs(lastAt)}</Text>
                    {live?.status === 'error' && <Badge tone="critical">Last run failed</Badge>}
                    {live?.status === 'stalled' && <Badge tone="warning">{`Stalled at ${live.phase || 'unknown step'}`}</Badge>}
                </BlockStack>
            )}
            <div>
                <Button size="slim" onClick={() => syncStore(storeId, type, setToast)} disabled={running} loading={running}>
                    {label}
                </Button>
            </div>
        </BlockStack>
    );
}

async function syncStore(storeId, type, setToast) {
    try {
        await api.startSync(type, storeId);
        setToast(`${type[0].toUpperCase() + type.slice(1)} sync started`);
    } catch (e) {
        setToast(e.message || `Failed to start the ${type} sync`);
    }
}
